import io from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { SOCKET_URL } from '../config';

// Socket.io Service
class SocketService {
  private socket: any = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;

  // Socket event listeners
  private eventListeners: Map<string, Function[]> = new Map();

  constructor() {
    this.connect();
  }

  // Connect to socket server
  private connect() {
    this.socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    this.setupEventListeners();
  }

  // Setup socket event listeners
  private setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('socket-connected', { socketId: this.socket?.id });
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('Socket disconnected:', reason);
      this.isConnected = false;
      this.emit('socket-disconnected', { reason });
      
      // Attempt to reconnect
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => {
          this.reconnectAttempts++;
          this.connect();
        }, this.reconnectDelay * this.reconnectAttempts);
      }
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('Socket connection error:', error);
      this.emit('socket-error', { error: error.message });
    });

    // ==================== CALL EVENTS ====================
    this.socket.on('incoming-call', (data: any) => {
      console.log('Incoming call:', data);
      this.emit('incoming-call', data);
    });

    this.socket.on('call-status-update', (data: any) => {
      console.log('Call status update:', data);
      this.emit('call-status-update', data);
    });

    this.socket.on('call-accepted', (data: any) => {
      console.log('Call accepted:', data);
      this.emit('call-accepted', data);
    });

    this.socket.on('call-rejected', (data: any) => {
      console.log('Call rejected:', data);
      this.emit('call-rejected', data);
    });

    this.socket.on('call-ended', (data: any) => {
      console.log('Call ended:', data);
      this.emit('call-ended', data);
    });

    this.socket.on('call-initiated', (data: any) => {
      console.log('Call initiated:', data);
      this.emit('call-initiated', data);
    });

    this.socket.on('call-update', (data: any) => {
      console.log('Call update:', data);
      this.emit('call-update', data);
    });

    this.socket.on('call-answered', (data: any) => {
      console.log('Call answered:', data);
      this.emit('call-answered', data);
    });

    this.socket.on('call-held', (data: any) => {
      console.log('Call held:', data);
      this.emit('call-held', data);
    });

    this.socket.on('call-resumed', (data: any) => {
      console.log('Call resumed:', data);
      this.emit('call-resumed', data);
    });

    this.socket.on('call-transferred', (data: any) => {
      console.log('Call transferred:', data);
      this.emit('call-transferred', data);
    });

    // ==================== AUDIO EVENTS ====================
    this.socket.on('audio-stream-start', (data: any) => {
      console.log('Audio stream start:', data);
      this.emit('audio-stream-start', data);
    });

    this.socket.on('phone-audio-data', (data: any) => {
      this.emit('phone-audio-data', data);
    });

    this.socket.on('microphone-muted', () => {
      console.log('Microphone muted');
      this.emit('microphone-muted', {});
    });

    this.socket.on('microphone-unmuted', () => {
      console.log('Microphone unmuted');
      this.emit('microphone-unmuted', {});
    });

    // ==================== AGENT EVENTS ====================
    this.socket.on('agent-status-update', (data: any) => {
      console.log('Agent status update:', data);
      this.emit('agent-status-update', data);
    });

    this.socket.on('agent-status-change', (data: any) => {
      console.log('Agent status change:', data);
      this.emit('agent-status-change', data);
    });

    this.socket.on('status-updated', (data: any) => {
      console.log('Status updated:', data);
      this.emit('status-updated', data);
    });

    this.socket.on('agent-login', (data: any) => {
      console.log('Agent login:', data);
      this.emit('agent-login', data);
    });

    this.socket.on('agent-logout', (data: any) => {
      console.log('Agent logout:', data);
      this.emit('agent-logout', data);
    });

    // ==================== QUEUE EVENTS ====================
    this.socket.on('queue-status-update', (data: any) => {
      console.log('Queue status update:', data);
      this.emit('queue-status-update', data);
    });

    this.socket.on('queue-call-assigned', (data: any) => {
      console.log('Queue call assigned:', data);
      this.emit('queue-call-assigned', data);
    });

    this.socket.on('queue-call-completed', (data: any) => {
      console.log('Queue call completed:', data);
      this.emit('queue-call-completed', data);
    });

    // ==================== SUPERVISOR EVENTS ====================
    this.socket.on('supervisor-dashboard-update', (data: any) => {
      console.log('Supervisor dashboard update:', data);
      this.emit('supervisor-dashboard-update', data);
    });

    this.socket.on('agent-performance-update', (data: any) => {
      console.log('Agent performance update:', data);
      this.emit('agent-performance-update', data);
    });

    this.socket.on('real-time-stats', (data: any) => {
      console.log('Real-time stats:', data);
      this.emit('real-time-stats', data);
    });

    // ==================== NOTIFICATION EVENTS ====================
    this.socket.on('notification', (data: any) => {
      console.log('Notification:', data);
      this.emit('notification', data);
    });

    this.socket.on('alert', (data: any) => {
      console.log('Alert:', data);
      this.emit('alert', data);
    });

    this.socket.on('system-message', (data: any) => {
      console.log('System message:', data);
      this.emit('system-message', data);
    });

    // ==================== RECORDING EVENTS ====================
    this.socket.on('recording-started', (data: any) => {
      console.log('Recording started:', data);
      this.emit('recording-started', data);
    });

    this.socket.on('recording-stopped', (data: any) => {
      console.log('Recording stopped:', data);
      this.emit('recording-stopped', data);
    });

    this.socket.on('recording-error', (data: any) => {
      console.log('Recording error:', data);
      this.emit('recording-error', data);
    });

    // ==================== CUSTOMER EVENTS ====================
    this.socket.on('customer-data-updated', (data: any) => {
      console.log('Customer data updated:', data);
      this.emit('customer-data-updated', data);
    });

    this.socket.on('customer-note-added', (data: any) => {
      console.log('Customer note added:', data);
      this.emit('customer-note-added', data);
    });

    // ==================== SYSTEM EVENTS ====================
    this.socket.on('active-calls', (data: any) => {
      console.log('Active calls:', data);
      this.emit('active-calls', data);
    });

    this.socket.on('waiting-calls', (data: any) => {
      console.log('Waiting calls:', data);
      this.emit('waiting-calls', data);
    });

    this.socket.on('system-health', (data: any) => {
      console.log('System health:', data);
      this.emit('system-health', data);
    });

    // ==================== ERROR EVENTS ====================
    this.socket.on('call-error', (data: any) => {
      console.error('Call error:', data);
      this.emit('call-error', data);
    });

    this.socket.on('status-error', (data: any) => {
      console.error('Status error:', data);
      this.emit('status-error', data);
    });

    this.socket.on('system-error', (data: any) => {
      console.error('System error:', data);
      this.emit('system-error', data);
    });
  }

  // Emit event to listeners
  private emit(event: string, data: any) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Add event listener
  public on(event: string, callback: Function) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)?.push(callback);
  }

  // Remove event listener
  public off(event: string, callback: Function) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  // ==================== ROOM MANAGEMENT ====================
  
  // Join user room
  public joinUserRoom(userId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-user-room', userId);
    }
  }

  // Join supervisor room
  public joinSupervisorRoom() {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-supervisor-room');
    }
  }

  // Join queue room
  public joinQueueRoom(queueId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-queue-room', queueId);
    }
  }

  // Leave queue room
  public leaveQueueRoom(queueId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave-queue-room', queueId);
    }
  }

  // ==================== CALL MANAGEMENT ====================

  // Start call
  public startCall(phoneNumber: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('start-call', { phoneNumber });
    }
  }

  // Make call
  public makeCall(phoneNumber: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('make-call', { phoneNumber });
    }
  }

  // Answer call
  public answerCall(callId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('answer-call', { callId });
    }
  }

  // Accept call
  public acceptCall(callId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('accept-call', { callId });
    }
  }

  // Reject call
  public rejectCall(callId: string, reason?: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('reject-call', { callId, reason });
    }
  }

  // End call
  public endCall(callId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('end-call', { callId });
    }
  }

  // Transfer call
  public transferCall(callId: string, targetAgentId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('transfer-call', { callId, targetAgentId });
    }
  }

  // ==================== AUDIO MANAGEMENT ====================

  // Mute microphone
  public muteMicrophone() {
    if (this.socket && this.isConnected) {
      this.socket.emit('mute-microphone');
    }
  }

  // Unmute microphone
  public unmuteMicrophone() {
    if (this.socket && this.isConnected) {
      this.socket.emit('unmute-microphone');
    }
  }

  // Hold call
  public holdCall(callId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('hold-call', { callId });
    }
  }

  // Resume call
  public resumeCall(callId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('resume-call', { callId });
    }
  }

  // Send audio data
  public sendAudioData(data: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit('audio-data', data);
    }
  }

  // ==================== AGENT MANAGEMENT ====================

  // Update agent status
  public updateAgentStatus(agentId: string, status: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent-status-change', { agentId, status });
    }
  }

  // Agent login
  public agentLogin(agentId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent-login', { agentId });
    }
  }

  // Agent logout
  public agentLogout(agentId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent-logout', { agentId });
    }
  }

  // ==================== QUEUE MANAGEMENT ====================

  // Update queue status
  public updateQueueStatus(queueId: string, status: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('queue-status-update', { queueId, status });
    }
  }

  // Assign call to queue
  public assignCallToQueue(callId: string, queueId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('assign-call-to-queue', { callId, queueId });
    }
  }

  // ==================== SUPERVISOR FEATURES ====================

  // Request real-time stats
  public requestRealTimeStats() {
    if (this.socket && this.isConnected) {
      this.socket.emit('request-real-time-stats');
    }
  }

  // Request agent performance
  public requestAgentPerformance(agentId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('request-agent-performance', { agentId });
    }
  }

  // Broadcast supervisor message
  public broadcastSupervisorMessage(message: string, type: string = 'info') {
    if (this.socket && this.isConnected) {
      this.socket.emit('supervisor-broadcast', { message, type });
    }
  }

  // ==================== NOTIFICATION SYSTEM ====================

  // Send notification
  public sendNotification(userId: string, message: string, type: string = 'info') {
    if (this.socket && this.isConnected) {
      this.socket.emit('send-notification', { userId, message, type });
    }
  }

  // Send alert
  public sendAlert(userId: string, message: string, severity: string = 'warning') {
    if (this.socket && this.isConnected) {
      this.socket.emit('send-alert', { userId, message, severity });
    }
  }

  // Send system message
  public sendSystemMessage(message: string, target: string = 'all') {
    if (this.socket && this.isConnected) {
      this.socket.emit('send-system-message', { message, target });
    }
  }

  // ==================== RECORDING MANAGEMENT ====================

  // Start recording
  public startRecording(callId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('start-recording', { callId });
    }
  }

  // Stop recording
  public stopRecording(callId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('stop-recording', { callId });
    }
  }

  // ==================== CUSTOMER MANAGEMENT ====================

  // Update customer data
  public updateCustomerData(customerId: string, data: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit('update-customer-data', { customerId, data });
    }
  }

  // Add customer note
  public addCustomerNote(customerId: string, note: string, agentId: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('add-customer-note', { customerId, note, agentId });
    }
  }

  // ==================== SYSTEM MONITORING ====================

  // Get active calls
  public getActiveCalls() {
    if (this.socket && this.isConnected) {
      this.socket.emit('get-active-calls');
    }
  }

  // Get waiting calls
  public getWaitingCalls() {
    if (this.socket && this.isConnected) {
      this.socket.emit('get-waiting-calls');
    }
  }

  // Get system health
  public getSystemHealth() {
    if (this.socket && this.isConnected) {
      this.socket.emit('get-system-health');
    }
  }

  // ==================== LEGACY METHODS (for backward compatibility) ====================

  // Join room (legacy)
  public joinRoom(room: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-room', room);
    }
  }

  // Leave room (legacy)
  public leaveRoom(room: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave-room', room);
    }
  }

  // Update status (legacy)
  public updateStatus(agentId: string, status: string) {
    if (this.socket && this.isConnected) {
      this.socket.emit('update-status', { agentId, status });
    }
  }

  // Send call status (legacy)
  public sendCallStatus(data: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit('call-status', data);
    }
  }

  // Send audio stream started (legacy)
  public sendAudioStreamStarted(data: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit('audio-stream-started', data);
    }
  }

  // Get connection status
  public getConnectionStatus(): boolean {
    return this.isConnected;
  }

  // Get socket ID
  public getSocketId(): string | undefined {
    return this.socket?.id;
  }

  // Disconnect
  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // Reconnect
  public reconnect() {
    this.disconnect();
    this.reconnectAttempts = 0;
    this.connect();
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
